import { atom, useAtomValue, useSetAtom } from 'jotai'
import { format } from '~/lib/date/date'
import type { PaymentCardId } from '~/lib/graphql/scalars'
import { error } from '~/lib/log'
import { createPayjpNewCardToken$, userStatus$ } from '../store/cancelGuard'
import { deletedCardIds$, selectedCardStatus$ } from '../store/card'
import { deletePaymentCard } from '../store/deletePaymentCard'
import { cards$, plan$, transition$ } from '../store/stateMachine/state'
import { submitted$, tokenError$ } from './card-fusuma'
import { scope } from './scope'

/** カードふすまで、登録済カードを選択 or 新しいカード情報を入力し「決定」ボタンを押したときの処理 */
const submitPayjpCard$ = atom(null, async (get, set) => {
  const selected = get(selectedCardStatus$)
  if (selected === undefined) {
    return
  }

  // 前回失敗したときのトークンエラーが残っている場合はクリア
  if (get(tokenError$) !== undefined) {
    set(tokenError$, undefined)
  }

  // 登録済カードを選択する場合はトークンを生成する必要がないため、updated event を発行する
  if (selected.type === 'registered') {
    set(transition$, {
      type: 'updated',
      card: { type: 'registered', cardId: selected.cardId },
    })
    set(submitted$, 'finished')
  }

  // 新しいカードを登録する場合はここでトークン生成を行い、成功すれば updated event を発行する
  if (selected.type === 'new') {
    set(submitted$, 'pending')
    try {
      const newCard = await set(createPayjpNewCardToken$)
      set(transition$, {
        type: 'updated',
        card: { type: 'new', newCard },
        userStatus: get(userStatus$),
      })
      set(submitted$, 'finished')
    } catch (e) {
      if (e instanceof Error) {
        set(tokenError$, e.message)
      } else {
        error(e)
        set(tokenError$, 'カード情報の登録に失敗しました。')
      }
      set(submitted$, 'initial')
    }
  }
})
submitPayjpCard$.debugLabel = 'submitPayjpCard$'

export function useSetSubmitPayjpCard() {
  return useSetAtom(submitPayjpCard$)
}

const isCancelGuardPlan$ = atom((get) => {
  return get(plan$)?.isCancelGuard
})
isCancelGuardPlan$.debugLabel = 'isCancelGuardPlan$'

export function useIsCancelGuardPlan() {
  return useAtomValue(isCancelGuardPlan$)
}

/** カードふすまで、登録済カードを削除する処理
 *
 * 選択中のカードが削除されて別のカードに選択が移った場合は
 * カードふすま上でのカード選択状態と context.card を一致させるために
 * 削除後に updated event を発行して context.card の更新も行う
 */
const deletePayjpCard$ = atom(null, async (get, set, cardId: string) => {
  set(submitted$, 'pending')
  try {
    const res = await deletePaymentCard({ cardId: cardId as PaymentCardId })
    if (res === null) {
      set(deleteCardResult$, { type: 'completed' })
      set(deletedCardIds$, (prev) => [...prev, cardId])

      const selected = get(selectedCardStatus$)
      const cards = get(cards$)
      // 選択中のカードが削除された場合
      if (selected !== undefined && selected.type === 'registered' && selected.cardId === cardId) {
        // 別の登録済カードがある場合は、先頭のカードを選択状態にする
        if (cards.length > 0) {
          const nextCardId = cards[0].cardId
          set(selectedCardStatus$, { type: 'registered', cardId: nextCardId })
          set(transition$, {
            type: 'updated',
            card: { type: 'registered', cardId: nextCardId },
          })
          set(submitted$, 'initial')
          return
        } else {
          // すべてのカードが削除された場合は、削除済を伝える
          set(selectedCardStatus$, { type: 'deleted' })
          set(transition$, {
            type: 'updated',
            userStatus: get(userStatus$),
          })
          // カードふすまを閉じる
          set(submitted$, 'finished')
          return
        }
      }
      set(submitted$, 'initial')
    } else {
      const deletableDateFrom = format(res, 'yyyy年M月d日(E)')
      set(deleteCardResult$, {
        type: 'error',
        message: `こちらのカード情報を利用した予約があります。${deletableDateFrom} 以降に削除をお試しください。`,
      })
      set(submitted$, 'initial')
    }
  } catch (e) {
    set(deleteCardResult$, {
      type: 'error',
      message: 'カードの削除に失敗しました。しばらく時間をおいてから、再度お試し下さい。',
    })
    set(submitted$, 'initial')
  }
})
deletePayjpCard$.debugLabel = 'deletePayjpCard$'

export function useSetDeletePayjpCard() {
  return useSetAtom(deletePayjpCard$)
}

/** カードふすまでカードを削除した時の結果 */
type DeleteCardResult = { type: 'error'; message: string } | { type: 'completed' }

const deleteCardResult$ = scope.atom<DeleteCardResult | undefined>(undefined, 'deleteCardResult$')

export function useDeleteCardResult() {
  return useAtomValue(deleteCardResult$)
}

export function maskedNumberDisplay(maskedNumber: string) {
  // 1234 -> **** 1234 という表示にする
  // 引数の maskedNumber は基本的に4桁になるはずだが、それ以外の桁数の場合も考慮しておく
  return `**** ${maskedNumber.slice(-4).padStart(4, '*')}`
}
if (import.meta.vitest) {
  const { expect, it } = import.meta.vitest
  it('maskedNumberDisplay', () => {
    expect(maskedNumberDisplay('12345')).toBe('**** 2345')
    expect(maskedNumberDisplay('1234')).toBe('**** 1234')
    expect(maskedNumberDisplay('123')).toBe('**** *123')
    expect(maskedNumberDisplay('12')).toBe('**** **12')
    expect(maskedNumberDisplay('1')).toBe('**** ***1')
    expect(maskedNumberDisplay('')).toBe('**** ****')
  })
}
